import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * Renders the products stored by `RecentlyViewed` through the Section Rendering API.
 *
 * The section can't be rendered server-side because the viewed products only live in localStorage,
 * so the initial render is a skeleton that gets hydrated once the section scrolls into view.
 */
class RecentlyViewedProducts extends Component {
  /**
   * The observer for the recently viewed products
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-products-performed attribute has been set to 'true'
      if (mutation.attributeName === 'data-products-performed' && this.dataset.productsPerformed === 'true') continue;

      // All other attribute changes trigger a reload
      this.#loadProducts();
      break;
    }
  });

  connectedCallback() {
    super.connectedCallback();

    // The Theme Editor renders example products, which stay untouched so the section doesn't
    // swap its content while the merchant is editing it.
    if (Shopify.designMode) return;

    this.#reserveSpace();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * The recently viewed product ids, without the product currently being viewed
   * @returns {string[]}
   */
  get #productIds() {
    const { currentProductId, maxProducts } = this.dataset;
    const limit = Number(maxProducts) || 4;

    try {
      return RecentlyViewed.getProducts()
        .filter((id) => id && id !== currentProductId)
        .slice(0, limit);
    } catch {
      return [];
    }
  }

  /**
   * Matches the placeholder to what is about to be rendered, so the section doesn't resize once the
   * products are loaded. Runs before the section is scrolled into view, hence the early hiding.
   */
  #reserveSpace() {
    const productCount = this.#productIds.length;

    if (productCount === 0) {
      this.#handleEmpty();
      return;
    }

    const skeleton = this.querySelector('[data-skeleton]');
    if (!skeleton) return;

    const skeletonItems = [...skeleton.querySelectorAll('.recently-viewed-products__skeleton-item')];
    const firstItem = skeletonItems[0];
    if (!firstItem) return;

    // Carousels only ever show a single row, the grid grows with the number of products
    const target = skeleton.hasAttribute('data-skeleton-single-row')
      ? Math.min(productCount, skeletonItems.length)
      : productCount;

    for (let index = skeletonItems.length - 1; index >= target; index--) {
      skeletonItems[index]?.remove();
    }

    for (let index = skeletonItems.length; index < target; index++) {
      skeleton.appendChild(firstItem.cloneNode(true));
    }
  }

  /**
   * Load the recently viewed products
   */
  #loadProducts() {
    const { productsPerformed, sectionId } = this.dataset;

    if (!sectionId) {
      throw new Error('A section ID is required');
    }

    // Already hydrated, accounts for the case where the Theme Editor loads the section from the
    // editor's visual preview context.
    if (productsPerformed === 'true') return;

    const productIds = this.#productIds;

    if (productIds.length === 0) {
      this.#handleEmpty();
      return;
    }

    sectionRenderer
      .getSectionHTML(sectionId, false, this.#buildUrl(productIds))
      .then((html) => {
        if (!html?.trim().length) {
          this.#handleEmpty();
          return;
        }

        this.dataset.productsPerformed = 'true';
        morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });

        if (!this.querySelector('[data-has-products="true"]')) {
          this.#handleEmpty();
        }
      })
      .catch((error) => {
        this.#handleError(error);
      });
  }

  /**
   * Builds the search URL used to render the section with the viewed products
   * @param {string[]} productIds
   * @returns {URL}
   */
  #buildUrl(productIds) {
    const url = new URL(this.dataset.url || '/search', window.location.origin);

    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return url;
  }

  /**
   * Hides the section when there is nothing to show
   */
  #handleEmpty() {
    // Keep the placeholders in the Theme Editor so the section stays editable
    if (Shopify.designMode) return;

    this.classList.add('hidden');
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
